import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "web-design-system";
import { useRouter } from "@tanstack/react-router";
import { Route as PortfolioRoute } from "@/routes/(auth)/_headed/clients/$id/portfolio";
import { usePortfolioSummaryV2 } from "creso-core/hooks";
import { PortfolioSource } from "creso-core/types";
import { isActiveClient } from "creso-core/utils";
import { HoldingsComponent } from "./holdings";
import { SIPComponent } from "./sip";
import { SWPComponent } from "./swp";
import { STPComponent } from "./stp";
import { OrdersComponent } from "./orders";
import { PortfolioImportStatusComponent } from "./summary";

export function PortfolioDetails() {
  const { detail = "holdings" } = PortfolioRoute.useSearch();
  const { client } = PortfolioRoute.useLoaderData();
  const router = useRouter();

  const { data: summary } = usePortfolioSummaryV2(
    client?.investment_account ?? ""
  );

  const cresoPortfolioSummary = summary?.find(
    (p) => p.source_type === "INTERNAL"
  );

  const externalPorfolioSummary = summary?.find(
    (p) => p.source_type === "EXTERNAL"
  );

  const hasExternalSummary =
    Number(externalPorfolioSummary?.invested_value) > 0;

  const showOverallSummary =
    hasExternalSummary && Number(cresoPortfolioSummary?.invested_value) > 0;

  const {
    source = showOverallSummary
      ? "OVERALL"
      : hasExternalSummary
        ? "EXTERNAL"
        : "INTERNAL",
  } = PortfolioRoute.useSearch();

  return (
    <div className={cn("flex flex-row flex-1")}>
      <Tabs
        value={detail}
        onValueChange={(value) => {
          router.navigate({
            to: ".",
            search: (prev) => ({
              ...prev,
              detail: value as "holdings" | "sip" | "swp" | "stp" | "orders",
            }),
            replace: true,
          });
        }}
        className="w-full flex flex-1 relative flex-col"
      >
        <div className="flex flex-row items-center gap-4 justify-between w-full">
          <TabsList className="mr-auto">
            <TabsTrigger value="holdings">Holdings</TabsTrigger>
            <TabsTrigger value="sip">SIP</TabsTrigger>
            <TabsTrigger value="swp">SWP</TabsTrigger>
            <TabsTrigger value="stp">STP</TabsTrigger>
            <TabsTrigger value="orders">Recent Orders</TabsTrigger>
          </TabsList>
          <TabsContent
            value="holdings"
            className="mt-0 flex items-center gap-3"
          >
            {client && (
              <PortfolioImportStatusComponent
                selectedTab={source}
                client={client}
              />
            )}
            <Tabs
              value={source}
              onValueChange={(value) => {
                router.navigate({
                  to: ".",
                  params: { id: client?.id.toString() },
                  search: (prev) => ({
                    ...prev,
                    source: value as PortfolioSource,
                  }),
                  replace: true,
                });
              }}
            >
              <TabsList>
                {showOverallSummary && (
                  <TabsTrigger value="OVERALL">All</TabsTrigger>
                )}
                {isActiveClient(client) ? (
                  <TabsTrigger value="INTERNAL">Internal</TabsTrigger>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-not-allowed">
                        <TabsTrigger
                          value="INTERNAL"
                          disabled
                          className="pointer-events-none"
                        >
                          Internal
                        </TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      Activate the account to start transacting.
                    </TooltipContent>
                  </Tooltip>
                )}
                <TabsTrigger value="EXTERNAL">External</TabsTrigger>
              </TabsList>
            </Tabs>
          </TabsContent>
        </div>
        <TabsContent className="flex flex-col flex-1 mt-4" value="holdings">
          <HoldingsComponent />
        </TabsContent>
        <TabsContent className="flex flex-col flex-1 gap-4 mt-4" value="sip">
          <SIPComponent />
        </TabsContent>
        <TabsContent className="flex flex-col flex-1 gap-4 mt-4" value="swp">
          <SWPComponent />
        </TabsContent>
        <TabsContent className="flex flex-col flex-1 gap-4 mt-4" value="stp">
          <STPComponent />
        </TabsContent>
        <TabsContent className="flex flex-col flex-1 gap-4 mt-4" value="orders">
          <OrdersComponent />
        </TabsContent>
      </Tabs>
    </div>
  );
}
